import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { doctorAPI, paymentAPI, earningsAPI } from '../../../utils/api';
import { useNotification } from '../../../context/NotificationContext';

const ProviderWallet = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [balance, setBalance] = useState(0);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [bankDetails, setBankDetails] = useState({
    bankName: '',
    ifscCode: '',
    accountNumber: '',
    confirmAccountNumber: ''
  });
  const [loading, setLoading] = useState(true);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [showConfirmAccountNumber, setShowConfirmAccountNumber] = useState(false);

  // Earnings History State
  const [earnings, setEarnings] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  useEffect(() => {
    fetchWalletData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);

      // Fetch Balance
      const balanceRes = await doctorAPI.getWallet();
      if (balanceRes.data?.success) {
        setBalance(balanceRes.data.data?.balance || user?.wallet_balance || 0);
      }

      // Fetch Earnings History
      try {
        const historyRes = await earningsAPI.getDoctorEarnings({
          page: pagination.page,
          limit: 10
        });

        if (historyRes.data?.success) {
          setEarnings(historyRes.data.data.earnings);
          setTotalEarned(historyRes.data.data.totalEarned);
          setPagination(prev => ({
            ...prev,
            totalPages: historyRes.data.data.totalPages,
            total: historyRes.data.data.total
          }));
        }
      } catch (err) {
        console.warn('Failed to fetch earnings history, might not be implemented securely yet', err);
      }

    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (parseFloat(withdrawAmount) > balance) {
      showError('Insufficient balance');
      return;
    }

    if (!bankDetails.bankName || !bankDetails.ifscCode || !bankDetails.accountNumber) {
      showError('Please fill in all bank details');
      return;
    }

    if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
      showError('Account numbers do not match');
      return;
    }

    try {
      const res = await paymentAPI.requestWithdrawal({
        amount: parseFloat(withdrawAmount),
        bank_name: bankDetails.bankName,
        ifsc_code: bankDetails.ifscCode,
        account_number: bankDetails.accountNumber
      });
      if (res.data?.success) {
        showSuccess('Withdrawal request submitted successfully');
        setWithdrawAmount('');
        setBankDetails({
          bankName: '',
          ifscCode: '',
          accountNumber: '',
          confirmAccountNumber: ''
        });
        fetchWalletData();
      } else {
        showError(res.data?.message || 'Failed to submit withdrawal request');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to submit withdrawal request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'credited':
        return <span className="px-2 py-1 bg-green-900/50 text-green-400 text-xs rounded-full">Credited</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-900/50 text-yellow-400 text-xs rounded-full">Pending</span>;
      case 'failed':
        return <span className="px-2 py-1 bg-red-900/50 text-yellow-400 text-xs rounded-full">Failed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">{status}</span>;
    }
  };

  if (loading && pagination.page === 1) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        <p className="text-gray-400 mt-2">Loading wallet...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gold">My Wallet</h1>

      {/* Wallet Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-gold to-yellow-600 rounded-xl p-6 text-dark shadow-lg transform transition hover:scale-[1.02]">
          <div className="text-center">
            <p className="text-lg opacity-80 mb-2 font-medium">Available Balance</p>
            <p className="text-4xl font-bold">₹{balance.toFixed(2)}</p>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
          <div className="text-center">
            <p className="text-lg text-gray-400 mb-2 font-medium">Total Lifetime Earnings</p>
            <p className="text-4xl font-bold text-green-400">₹{totalEarned.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Withdrawal Section */}
      <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 text-white border-b border-gray-700 pb-2">Withdraw Earnings</h3>

        {user?.hospital_id ? (
          <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-4 text-center">
            <p className="text-blue-200 font-medium">Managed by Hospital</p>
            <p className="text-sm text-gray-400 mt-1">
              Your earnings are managed by your hospital administrator.
              Please contact them for payouts.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-gray-400 mb-2">Amount to Withdraw</label>
              <div className="relative">
                <span className="absolute left-4 top-3 text-gray-400">₹</span>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 pl-8 pr-4 text-light placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Bank Name</label>
                <input
                  type="text"
                  placeholder="e.g. HDFC Bank"
                  value={bankDetails.bankName}
                  onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
              <div>
                <label className="block text-gray-400 mb-2">IFSC Code</label>
                <input
                  type="text"
                  placeholder="e.g. HDFC0001234"
                  value={bankDetails.ifscCode}
                  onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value.toUpperCase() })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Account Number</label>
                <div className="relative">
                  <input
                    type={showAccountNumber ? "text" : "password"}
                    placeholder="Enter Account Number"
                    value={bankDetails.accountNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAccountNumber(!showAccountNumber)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                  >
                    {showAccountNumber ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-gray-400 mb-2">Confirm Account Number</label>
                <div className="relative">
                  <input
                    type={showConfirmAccountNumber ? "text" : "password"}
                    placeholder="Confirm Account Number"
                    value={bankDetails.confirmAccountNumber}
                    onChange={(e) => setBankDetails({ ...bankDetails, confirmAccountNumber: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-3 px-4 pr-10 text-light focus:outline-none focus:ring-2 focus:ring-gold"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmAccountNumber(!showConfirmAccountNumber)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gold transition-colors focus:outline-none"
                  >
                    {showConfirmAccountNumber ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-400 bg-gray-700/30 p-3 rounded-lg">
              <p>• Minimum withdrawal: ₹100</p>
              <p>• Processing time: 3-5 business days</p>
              <p>• Please double-check your bank details.</p>
            </div>
            <button
              onClick={handleWithdraw}
              disabled={
                !withdrawAmount ||
                parseFloat(withdrawAmount) <= 0 ||
                parseFloat(withdrawAmount) > balance ||
                !bankDetails.bankName ||
                !bankDetails.ifscCode ||
                !bankDetails.accountNumber ||
                bankDetails.accountNumber !== bankDetails.confirmAccountNumber
              }
              className="w-full bg-gold text-dark py-3 rounded-lg font-bold hover:bg-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Request Withdrawal
            </button>
          </div>
        )}
      </div>

      {/* Earnings History */}
      <div className="bg-gray-800 rounded-xl p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 text-white border-b border-gray-700 pb-2">Recent Earnings</h3>

        {earnings.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            No earnings history found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="pb-3 px-2">Date</th>
                  <th className="pb-3 px-2">User</th>
                  <th className="pb-3 px-2">Type</th>
                  <th className="pb-3 px-2 text-right">Amount</th>
                  <th className="pb-3 px-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {earnings.map((earning) => (
                  <tr key={earning._id} className="text-gray-300 hover:bg-gray-700/30 transition-colors">
                    <td className="py-3 px-2">
                      {new Date(earning.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                      <div className="text-xs text-gray-500">
                        {new Date(earning.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="py-3 px-2">
                      {earning.patientId?.name || 'Unknown User'}
                    </td>
                    <td className="py-3 px-2 capitalize">
                      {earning.consultationType} Session
                    </td>
                    <td className="py-3 px-2 text-right font-medium text-green-400">
                      ₹{earning.amount}
                    </td>
                    <td className="py-3 px-2 text-center">
                      {getStatusBadge(earning.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center mt-6 gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-gray-400">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 bg-gray-700 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderWallet;
